import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';
import styles from './styles.module.css';
import { Box, Button, Divider, Grid, IconButton, Paper, Typography, Tooltip } from '@mui/material';
import { BP_COLOR } from '../../bp-theme';
import { TripOrigin, Undo } from '@mui/icons-material';
import { useState } from 'react';
import { initiateInspectionReq } from '../../config/axios';
import { InspectionFormDialog } from './InspectionFormDialog';
import { useNotify } from '../../hooks/use-notify';
import { useAuthentication } from '../../hooks/use-authentication';

export const MapViewer = () => {
  useAuthentication();
  const [departmentName, setDepartmentName] = useState('');
  const [selectedZone, setSelectedZone] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const notify = useNotify();
  const [geoUrl, setGeoUrl] = useState('https://raw.githubusercontent.com/gregoiredavid/france-geojson/master/departements-version-simplifiee.geojson');

  const handleClick = geo => {
    if (!departmentName) {
      const { code, nom } = geo.properties;
      const path = `${code}-${nom
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()}`;
      setGeoUrl(`https://raw.githubusercontent.com/gregoiredavid/france-geojson/master/departements/${path}/communes-${path}.geojson`);

      setDepartmentName(nom);
    } else {
      setSelectedZone(geo);
    }
  };

  const initiateInspection = async (email, itemTypes) => {
    const { geometry, properties } = selectedZone;
    const request = {
      geometry: {
        type: geometry.type,
        coordinates: [geometry.coordinates[0].map(([longitude, latitude]) => ({ latitude, longitude }))],
      },
      email,
      zoneName: properties.nom,
    };
    try {
      await initiateInspectionReq(request, itemTypes);
      notify('La demande est en cours de traitement.', { type: 'success' });
    } catch {
      notify("Une erreur s'est produite.", { type: 'error' });
    }
  };

  const unsetDepartment = () => {
    setGeoUrl('https://raw.githubusercontent.com/gregoiredavid/france-geojson/master/departements-version-simplifiee.geojson');
    setDepartmentName('');
    setSelectedZone(null);
  };

  const dialogHandler = () => {
    setIsDialogOpen(!isDialogOpen);
  };

  return (
    <div className='box-style'>
      {selectedZone && (
        <InspectionFormDialog commune={selectedZone.properties.nom} open={isDialogOpen} handleClose={dialogHandler} handleSubmit={initiateInspection} />
      )}
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <Box py={4} px={2}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Box>
                {departmentName ? (
                  <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant='h5' color='#727272' textTransform='uppercase'>
                      {departmentName}
                    </Typography>
                    <Tooltip title='Revenir en arrière'>
                      <IconButton onClick={unsetDepartment}>
                        <Undo />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  <Typography variant='h4'>France</Typography>
                )}
                <Divider />
                <Box my={3} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', color: BP_COLOR['20'], height: 'fit-content' }}>
                  <TripOrigin />
                  <Typography variant='h5' mx={2} my={3}>
                    {!departmentName ? 'Départements' : 'Communes'}
                  </Typography>
                </Box>
                {selectedZone && (
                  <Typography variant='h6' textTransform='uppercase' color='#949494' mb={3} ml={4}>
                    {selectedZone.properties.nom}
                  </Typography>
                )}
                <Box textAlign='center' px='auto'>
                  <Button onClick={dialogHandler} disabled={!selectedZone}>
                    Inspecter
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Grid>
        <Grid item xs={9}>
          <Box py={4}>
            <div className={styles.mapContainer}>
              <ComposableMap
                projectionConfig={{
                  rotate: [-10.0, -52.0, 0],
                  center: [-5, -7],
                  scale: 5000,
                }}
              >
                <ZoomableGroup center={[0, 0]} zoom={1} minZoom={1} maxZoom={10}>
                  <Geographies geography={geoUrl} stroke='#FFFFFF' strokeWidth={0.1}>
                    {({ geographies }) =>
                      geographies.map(geo => {
                        return (
                          <Geography
                            onClick={() => handleClick(geo)}
                            key={geo.rsmKey}
                            geography={geo}
                            elevation={1000}
                            style={{
                              default: {
                                fill: geo.rsmKey === selectedZone?.rsmKey ? '#69ff90' : '#9cd2ff',
                                outline: 'none',
                                stroke: geo.rsmKey === selectedZone?.rsmKey ? '#00e63d' : '#ffffff00',
                              },
                              hover: {
                                fill: geo.rsmKey === selectedZone?.rsmKey ? '#69ff90' : '#6cbbfc',
                                outline: 'none',
                                stroke: geo.rsmKey === selectedZone?.rsmKey ? '#00e63d' : '#fff',
                              },
                              pressed: { fill: '#ff0000', stroke: '1' },
                            }}
                          />
                        );
                      })
                    }
                  </Geographies>
                </ZoomableGroup>
              </ComposableMap>
            </div>
          </Box>
        </Grid>
      </Grid>
    </div>
  );
};
